import { Helmet } from 'react-helmet-async';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// @mui
import Grid from '@mui/material/Grid2';
import { Box, Stack, Button, Popover, MenuItem, Container, LinearProgress, TablePagination } from '@mui/material';

// components
import BulkRequestListUserViewItem from './UserViewItem';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import Dialog from '../../../components/dialog';
import { ListToolbar } from '../../../components/list';
import { /* Request */ type BulkRequestProps, BulkRequest } from '../../../models';
// import type { Status } from '../../../utils/request.types';
// import { fDateTime } from '../../../utils/formatTime';
// import { applySortFilter, getComparator, type Order } from '../../../utils/tableSorter';

import { useGetBulkRequestsQuery /* useDelBulkRequestMutation */ } from '../../../slices/apis/bulkRequestApi';
// import { useRole } from '../../../hooks/useRole';

// ----------------------------------------------------------------------

const findRequest = (requests: BulkRequestProps[], id: number) => {
	return requests.find((request) => Number(request.id) === id);
};

export default function BulkRequestsUser() {
	const { data = [], isFetching } = useGetBulkRequestsQuery();

	const requests = data as BulkRequestProps[];

	const [requestEditable, setRequestEditable] = useState(false);
	const [page, setPage] = useState(0);
	// const [order, setOrder] = useState<Order>('desc');
	// const [orderBy, setOrderBy] = useState<keyof BulkRequestProps>('createdAt');
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	// const [archivedRequest, setArchivedRequest] = useState(() => ['']);

	const [filterName, setFilterName] = useState('');
	const [selected] = useState<string[]>([]);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const navigate = useNavigate();
	// const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - requests.length) : 0;
	// const filteredRequests = applySortFilter({
	// 	list: requests,
	// 	comparator: getComparator(order, orderBy),
	// 	query: filterName,
	// 	orderBy: 'user.firstname',
	// });
	// const isNotFound = !filteredRequests.length && !!filterName;

	/*
    request deletion
  */
	const [selectedRequest, setSelectedRequest] = useState<BulkRequestProps | null>(null);
	// const [delRequest, { isSuccess: isDelReqSuccess, isLoading: isDelReqLoading }] = useDelBulkRequestMutation();
	const [requestDeleteConfirmDialogOpen, setRequestDeleteConfirmDialogOpen] = useState<boolean>(false);

	const handleNavigate = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			const route = e.currentTarget.getAttribute('data-route');
			if (route) {
				navigate(route);
			}
		},
		[navigate],
	);

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		const requestId = Number(event.currentTarget.getAttribute('data-request-id'));

		const request = findRequest(requests, requestId);

		if (request) {
			setAnchorEl(event.currentTarget);
			setSelectedRequest(request);
			setRequestEditable(true);
		}
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	// const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof BulkRequestProps) => {
	// 	const isAsc = orderBy === property && order === 'asc';
	// 	setOrder(isAsc ? 'desc' : 'asc');
	// 	setOrderBy(property);
	// };

	const handleChangePage = (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
		setPage(newPage);
		setTimeout(() => {
			window.scroll({ top: 0, behavior: 'smooth' });
		}, 0);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setRowsPerPage(Number.parseInt(event.target.value, 10));
	};

	const handleFilterByName = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setFilterName(event.target.value);
	};

	const handleEditRequest = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/bulk-requests/${selectedRequest.id}/edit`);
		}
	};

	const handleOpenRequestDetail = (e: React.MouseEvent<HTMLButtonElement>) => {
		const reqId = e.currentTarget.getAttribute('data-req-id');
		if (reqId) {
			navigate(`/dashboard/bulk-requests/${reqId}/view`);
		}
	};

	/*
    del request
  */
	// const handleDeleteRequest = () => {
	// 	setAnchorEl(null);
	// 	setRequestDeleteConfirmDialogOpen(true);
	// };

	const handleCloseRequestDeleteConfirmDialog = () => {
		setRequestDeleteConfirmDialogOpen(false);
	};

	// const onRequestDeleteConfirm = () => {
	// 	if (selectedRequest?.id) {
	// 		delRequest(selectedRequest.id);
	// 	}
	// };

	// useEffect(() => {
	// 	if (isDelReqSuccess) {
	// 		setRequestDeleteConfirmDialogOpen(false);
	// 	}
	// }, [isDelReqSuccess]);

	return (
		<>
			<Helmet>
				<title> Bulk Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Container maxWidth={false} disableGutters sx={{ px: 1, width: '100%' }}>
				<Stack spacing={4} mb={4}>
					{!isFetching && (
						<Box>
							<Button
								variant="contained"
								data-route="/dashboard/bulk-requests/new"
								onClick={handleNavigate}
								startIcon={<Iconify icon="eva:plus-fill" />}
							>
								New Bulk Request
							</Button>
						</Box>
					)}
				</Stack>

				{isFetching ? (
					<LinearProgress />
				) : (
					<>
						<ListToolbar
							numSelected={selected.length}
							filterName={filterName}
							onFilterName={handleFilterByName}
							placeHolderText={'Bulk request contact...'}
						/>

						<Scrollbar>
							<Grid container spacing={2} direction="row">
								{requests?.map((request, index) => {
									return (
										<BulkRequestListUserViewItem
											key={index}
											{...request}
											handleOpenMenu={handleOpenMenu}
											handleOpenRequestDetail={handleOpenRequestDetail}
										/>
									);
								})}
							</Grid>
						</Scrollbar>

						<TablePagination
							rowsPerPageOptions={[10, 20, 50, 100]}
							component="div"
							count={requests.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>

						{/* request menu options popover */}
						{!BulkRequest.isCancelled(selectedRequest?.status) && (
							<Popover
								open={Boolean(anchorEl)}
								onClose={handleCloseMenu}
								anchorEl={anchorEl}
								anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
								transformOrigin={{ vertical: 'top', horizontal: 'right' }}
								PaperProps={{
									sx: {
										p: 1,
										width: 140,
										'& .MuiMenuItem-root': {
											px: 1,
											typography: 'body2',
											borderRadius: 0.75,
										},
									},
								}}
							>
								{!requestEditable ? null : (
									<MenuItem onClick={handleEditRequest}>
										<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
										Edit
									</MenuItem>
								)}
							</Popover>
						)}

						{/* request delete confirm */}
						<Dialog
							open={Boolean(requestDeleteConfirmDialogOpen)}
							onClose={handleCloseRequestDeleteConfirmDialog}
							title={'Warning'}
						>
							{/* <Grid container spacing={2}>
                <Grid size={{ md: 12 }}>
                  <Typography variant="subtitle2">{`Confirm to delete request ${selectedRequest?.id}`}</Typography>
                </Grid>
                <Grid size={{ md: 6 }}>
                  <Button loading={isDelReqLoading} variant="outlined" color="primary" onClick={onRequestDeleteConfirm}>
                    <span>Confirm</span>
                  </Button>
                </Grid>
                <Grid size={{ md: 6 }}>
                  <Button color="error" variant="outlined" onClick={handleCloseRequestDeleteConfirmDialog}>
                    Cancel
                  </Button>
                </Grid>
              </Grid> */}
						</Dialog>
					</>
				)}
			</Container>
		</>
	);
}
